/**
 * b18-shundo-dex: Condensed Shundo Dex panel for the Profile trophy slot.
 *
 * Rendered when collectionFoci[0] === "shundo". Shows a sprite grid of caught
 * shundos (shiny-tinted, up to 8), a caught count, and a CTA to open the full
 * Shundo Dex on the Pokédex page.
 *
 * TODO(b18h-3): shundo entries currently live in dexStateStore (localStorage).
 * Once b18h-3 ships the variant-state sync endpoint, entries will persist to
 * the server and sync across devices automatically.
 *
 * No em-dashes. No hardcoded hex colours. No emojis. Nexus Dex tokens only.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Sparkles } from "lucide-react";
import {
  DEX_STATE_EVENT,
  listDexEntriesForVariant,
  variantCountSummary,
} from "@/lib/dexStateStore";
import { CaughtSprite } from "@/components/CaughtSprite";
import { InfoBlock, InfoBlockStat } from "@/components/InfoBlock";
import { speciesName } from "@/data/speciesNames";

const MAX_AVATAR_COUNT = 8;

export function ShundoTrophyPanel() {
  const [, setLocation] = useLocation();
  const [tick, setTick] = useState(0);

  // Re-read on every dex-state mutation so the panel stays live.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const bump = () => setTick((n) => n + 1);
    window.addEventListener(DEX_STATE_EVENT, bump);
    return () => window.removeEventListener(DEX_STATE_EVENT, bump);
  }, []);

  const caught = useMemo(() => {
    void tick;
    return listDexEntriesForVariant("shundo")
      .filter((e) => e.state === "present")
      .sort((a, b) => a.dexNumber - b.dexNumber);
  }, [tick]);

  const summary = useMemo(() => {
    void tick;
    return variantCountSummary("shundo");
  }, [tick]);

  const avatars = caught.slice(0, MAX_AVATAR_COUNT);

  return (
    <InfoBlock
      icon={Sparkles}
      title="Shiny Perfect Dex"
      subtitle="Your shiny perfects. Each one logged manually."
      trailing={
        <InfoBlockStat
          label="Caught"
          value={summary.present}
          data-testid="shundo-trophy-count"
        />
      }
      data-testid="shundo-trophy-panel"
    >

      {summary.present === 0 ? (
        <div
          className="rounded-lg p-4 text-xs leading-relaxed"
          style={{
            background: "var(--surface-sunken)",
            color: "var(--text-secondary)",
          }}
          data-testid="shundo-trophy-empty"
        >
          No shiny perfects yet. Open the Shiny Perfect Dex and tap "+ Add your first Shiny Perfect"
          when you catch a shiny 100%.
        </div>
      ) : (
        <ul
          className="flex flex-wrap gap-2"
          data-testid="shundo-trophy-grid"
        >
          {avatars.map((e) => (
            <li key={e.dexNumber}>
              <button
                type="button"
                // /pokemon/:dexNumber is the live species route. This panel
                // still pointed at the dead /dex/species/:id path the rest of
                // the app was cured of (see pokedexCellNavigation.test) and
                // 404'd on every tap (2026-08-25 device report).
                onClick={() => setLocation(`/pokemon/${e.dexNumber}`)}
                data-testid={`shundo-trophy-cell-${e.dexNumber}`}
                className="rounded-lg p-1 hover-elevate flex flex-col items-center"
                aria-label={`Open ${speciesName(e.dexNumber)}`}
              >
                {/* REAL shiny artwork, not the old blueTint hue-rotate over
                    the normal art — that filter painted shiny Noctowl navy
                    instead of gold (2026-08-25 device report). A shiny
                    perfect should look like the shiny the trainer caught. */}
                <CaughtSprite
                  id={e.dexNumber}
                  size={44}
                  shiny
                  ariaLabel={speciesName(e.dexNumber)}
                />
                <span
                  className="mt-0.5 w-full truncate text-center text-[8px] leading-tight"
                  style={{ color: "var(--text-tertiary)", maxWidth: 52 }}
                >
                  {speciesName(e.dexNumber)}
                </span>
              </button>
            </li>
          ))}
          {caught.length > MAX_AVATAR_COUNT && (
            <li
              className="flex items-center justify-center rounded-lg p-1"
              style={{ minWidth: 52 }}
            >
              <span
                className="text-[11px] font-bold tabular-nums"
                style={{ color: "var(--text-tertiary)" }}
              >
                +{caught.length - MAX_AVATAR_COUNT}
              </span>
            </li>
          )}
        </ul>
      )}

      <button
        type="button"
        onClick={() => setLocation("/dex?filter=shundo")}
        data-testid="shundo-trophy-open-dex"
        className="mt-3 w-full rounded-[var(--radius-pill)] py-2.5 text-[12px] font-bold"
        style={{
          background: "var(--accent-subtle)",
          color: "var(--accent-fill)",
          border: "1px solid color-mix(in srgb, var(--accent-fill) 30%, transparent)",
          minHeight: 40,
        }}
      >
        Open Shiny Perfect Dex
      </button>
    </InfoBlock>
  );
}
